import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, ArrowRight, Send } from "lucide-react";
import { z } from "zod";

const serviceRequestSchema = z.object({
  serviceType: z.string().min(1, "Service type is required").max(100, "Service type too long"),
  businessName: z.string().trim().min(1, "Business name is required").max(200, "Business name too long"),
  contactName: z.string().trim().min(1, "Contact name is required").max(100, "Contact name too long"),
  email: z.string().email("Invalid email address").max(255, "Email too long"),
  phone: z.string().max(20, "Phone number too long").optional(),
  description: z.string().trim().min(10, "Description must be at least 10 characters").max(2000, "Description too long"),
  budgetRange: z.string().optional(),
  preferredStartDate: z.string().optional(),
});

const services = [
  "Social Media Marketing",
  "SEO Optimization",
  "Content Creation",
  "Paid Advertising",
  "Brand Strategy",
  "Web Development",
  "Graphic Design",
  "Video Production",
];

export const ServiceRequestForm = ({ onSuccess }: { onSuccess?: () => void }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    serviceType: "",
    businessName: "",
    contactName: "",
    email: "",
    phone: "",
    description: "",
    budgetRange: "",
    preferredStartDate: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to submit a request.",
        variant: "destructive",
      });
      return;
    }

    // Validate form data
    try {
      serviceRequestSchema.parse(formData);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
        return;
      }
    }

    setLoading(true);

    try {
      const { error } = await supabase.from("service_requests").insert({
        client_id: user.id,
        service_type: formData.serviceType,
        business_name: formData.businessName,
        contact_name: formData.contactName,
        email: formData.email,
        phone: formData.phone,
        description: formData.description,
        budget_range: formData.budgetRange,
        preferred_start_date: formData.preferredStartDate || null,
      });

      if (error) throw error;

      toast({
        title: "Request submitted!",
        description: "We'll get back to you within 24 hours.",
      });

      // Reset form
      setFormData({
        serviceType: "",
        businessName: "",
        contactName: "",
        email: "",
        phone: "",
        description: "",
        budgetRange: "",
        preferredStartDate: "",
      });

      if (onSuccess) onSuccess();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="glass-card p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">Request a New Service</h2>
        <p className="text-muted-foreground">
          Tell us about your project and we'll get back to you within 24 hours
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="serviceType">Service Type *</Label>
          <Select
            value={formData.serviceType}
            onValueChange={(value) => setFormData({ ...formData, serviceType: value })}
            required
          >
            <SelectTrigger className="input-modern">
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {services.map((service) => (
                <SelectItem key={service} value={service}>
                  {service}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="businessName">Business Name *</Label>
            <Input
              id="businessName"
              className="input-modern"
              value={formData.businessName}
              onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contactName">Contact Name *</Label>
            <Input
              id="contactName"
              className="input-modern"
              value={formData.contactName}
              onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              className="input-modern"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              type="tel"
              className="input-modern"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Project Description *</Label>
          <Textarea
            id="description"
            rows={4}
            className="input-modern resize-none"
            placeholder="Tell us about your project, goals, and what you're looking to achieve..."
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="budgetRange">Budget Range</Label>
            <Select
              value={formData.budgetRange}
              onValueChange={(value) => setFormData({ ...formData, budgetRange: value })}
            >
              <SelectTrigger className="input-modern">
                <SelectValue placeholder="Select budget range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="$500-$1,000">$500 - $1,000</SelectItem>
                <SelectItem value="$1,000-$5,000">$1,000 - $5,000</SelectItem>
                <SelectItem value="$5,000-$10,000">$5,000 - $10,000</SelectItem>
                <SelectItem value="$10,000+">$10,000+</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="preferredStartDate">Preferred Start Date</Label>
            <Input
              id="preferredStartDate"
              type="date"
              className="input-modern"
              value={formData.preferredStartDate}
              onChange={(e) => setFormData({ ...formData, preferredStartDate: e.target.value })}
            />
          </div>
        </div>

        <Button type="submit" className="w-full btn-primary" disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="mr-2 h-4 w-4" />
              Submit Request
            </>
          )}
        </Button>
      </form>
    </Card>
  );
};
